import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { Button } from '../Button';
import { StepContainer } from '../StepContainer';
import { IOption } from '../../types';
import { TFormValues } from '../../App';
import { breakpoints, colors, formFields } from '../../constants';
import { CloseIcon } from '../../svg';

interface IHelperImageThumbnailContainer {
    imageOpen: boolean;
}

const HelperImageThumbnailContainer = styled.div<IHelperImageThumbnailContainer>`
    cursor: ${({ imageOpen }) => (imageOpen ? 'zoom-out' : 'zoom-in')};
`;
const HelperImageThumbnail = styled.img``;
const ThumbnailTitle = styled.h5``;
const HelperImageContainer = styled.div``;
const HelperImage = styled.img``;
const IconHolder = styled.div``;
const Row = styled.div``;
const DividerBox = styled.span``;
const ButtonContainer = styled.div``;

interface IProps {
    className?: string;
    options: IOption[];
    getFieldError: (fieldName: keyof TFormValues) => string | undefined;
    setFieldValue(field: keyof TFormValues, value: string): void;
    onStepButtonClick(option: IOption): void;
}

const BaseOrderNumberForm = ({
    className,
    options,
    getFieldError,
    setFieldValue,
    onStepButtonClick,
}: IProps) => {
    const [storeOrderNumberFirstPart, setStoreOrderNumberFirstPart] =
        useState('');
    const [storeOrderNumberSecondPart, setStoreOrderNumberSecondPart] =
        useState('');
    const [helperImageOpen, setHelperImageOpen] = useState(false);

    // Reconstruct and reset orderNumber if field values change
    useEffect(() => {
        // (e.g. 1234/1234)
        const orderNumber = [
            storeOrderNumberFirstPart,
            '/',
            storeOrderNumberSecondPart,
        ]
            .join('')
            .replace(/ /g, '');

        setFieldValue('orderNumber', orderNumber);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeOrderNumberFirstPart, storeOrderNumberSecondPart]);

    const orderNumberFieldStyles = {
        maxWidth: '132px',
        marginBottom: '20px',
    };

    const invoiceImageUrl =
        'https://grandvision.s3.amazonaws.com/gon/assets/contactformulier-factuur-gon.png';

    return (
        <StepContainer className={className}>
            <h3>
                Voer je <strong>factuurnummer</strong> in
            </h3>
            <HelperImageThumbnailContainer
                imageOpen={helperImageOpen}
                onClick={() => setHelperImageOpen(!helperImageOpen)}
            >
                <HelperImageThumbnail
                    alt="Voorbeeld factuur thumbnail"
                    src={invoiceImageUrl}
                />
                <ThumbnailTitle>
                    Waar vind ik mijn factuurnummer?
                </ThumbnailTitle>
            </HelperImageThumbnailContainer>
            {helperImageOpen && (
                <HelperImageContainer>
                    <IconHolder
                        onClick={() => setHelperImageOpen(!helperImageOpen)}
                    >
                        <CloseIcon />
                    </IconHolder>
                    <HelperImage
                        alt="Voorbeeld factuur"
                        src={invoiceImageUrl}
                        onClick={() => setHelperImageOpen(!helperImageOpen)}
                    />
                </HelperImageContainer>
            )}
            <Row>
                <TextField
                    error={!!getFieldError(formFields.orderNumber)}
                    helperText={getFieldError(formFields.orderNumber)}
                    inputProps={{ maxLength: 12 }}
                    label={'Vul cijfers in'}
                    sx={orderNumberFieldStyles}
                    type="tel"
                    value={storeOrderNumberFirstPart}
                    onChange={(e) =>
                        setStoreOrderNumberFirstPart(e.target.value)
                    }
                />
                <DividerBox>/</DividerBox>
                <TextField
                    error={!!getFieldError(formFields.orderNumber)}
                    helperText={getFieldError(formFields.orderNumber)}
                    inputProps={{ maxLength: 12 }}
                    label={'Vul cijfers in'}
                    sx={orderNumberFieldStyles}
                    type="tel"
                    value={storeOrderNumberSecondPart}
                    onChange={(e) =>
                        setStoreOrderNumberSecondPart(e.target.value)
                    }
                />
            </Row>
            <ButtonContainer>
                {options.map((option) => (
                    <Button
                        key={option.title}
                        secondary={!!option.secondaryButton}
                        onClick={() => onStepButtonClick(option)}
                    >
                        {option.title}
                    </Button>
                ))}
            </ButtonContainer>
        </StepContainer>
    );
};

export const OrderNumberForm = styled(BaseOrderNumberForm)`
    position: relative;

    h3 {
        margin-bottom: 24px;
    }

    ${HelperImageThumbnailContainer} {
        display: flex;
        margin-bottom: 24px;

        @media (${breakpoints.mediumMin}) {
            position: absolute;
            flex-direction: column;
            top: 50%;
            right: 32px;
            width: 120px;
            transform: translateY(-50%);
        }
    }

    ${HelperImageThumbnail} {
        width: 64px;
        margin-right: 12px;
        border-radius: 3px;
        border-top: 2px solid ${colors.black};

        @media (${breakpoints.mediumMin}) {
            border-top: 3px solid ${colors.black};
            width: 100%;
        }
    }

    ${ThumbnailTitle} {
        color: ${colors.greyDark};
        font-weight: 400;
        line-height: 1.2;
        margin: 8px 0 0;
        text-align: center;
    }

    ${HelperImageContainer} {
        position: absolute;
        width: 600px;
        max-width: 100vw;
        border-radius: 5px;
        border-top: 4px solid ${colors.black};
        box-shadow: 0px 0px 48px -12px rgba(0, 0, 0, 0.38);
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        line-height: 0;
        z-index: 2;
        cursor: zoom-out;
    }

    ${HelperImage} {
        width: 100%;
    }

    ${IconHolder} {
        position: absolute;
        right: 0;
        height: 36px;
        width: 36px;
        padding: 10px;
        cursor: pointer;
    }

    ${CloseIcon} {
        stroke: ${colors.black};
        height: 100%;
        width: 100%;
    }

    ${Row} {
        display: flex;
        justify-content: center;

        @media (${breakpoints.mediumMin}) {
            justify-content: flex-start;
        }
    }

    ${DividerBox} {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        margin: 0 10px;
        height: 56px;
        font-size: 20px;
    }

    ${Button} {
        margin-right: 12px;
        margin-top: 8px;
    }
`;
